import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ApiResponse,
  StoreSettingsExpectedState,
  StoreSettingsPatch,
  StoreSettingsResponse,
  StoreSettingsWriteField,
} from '@find-my-part/types';
import { resolveMediaSettings, resolvePublicLaunchMode } from '@find-my-part/types';
import { adminApi } from '../api/client';
import { useSession } from '../hooks/useSession';

type TenantSettingsStatus = 'loading' | 'ready' | 'error';

export interface TenantSettingsContextValue {
  status: TenantSettingsStatus;
  settings: StoreSettingsResponse | null;
  update: (
    patch: StoreSettingsPatch,
    expectedOverride?: Partial<StoreSettingsExpectedState>
  ) => Promise<ApiResponse<StoreSettingsResponse>>;
  /** Refresh the complete server snapshot without discarding a card's local draft. */
  refresh: () => Promise<ApiResponse<StoreSettingsResponse>>;
  rebaseExpected: (observed: Partial<StoreSettingsExpectedState>) => void;
}

const TenantSettingsContext = createContext<TenantSettingsContextValue | null>(null);
const TenantSettingsMutationContext = createContext(0);

export function TenantSettingsProvider({ children }: {
  children: React.ReactNode;
}): React.ReactElement {
  const { token } = useSession();
  const [status, setStatus] = useState<TenantSettingsStatus>('loading');
  const [settings, setSettings] = useState<StoreSettingsResponse | null>(null);
  const [mutationVersion, setMutationVersion] = useState(0);
  // React state alone is not a sufficient arbitration primitive here: update
  // and refresh promises can settle in either order before a render commits.
  // Keep the latest published observation synchronously addressable so every
  // completion is compared and merged against the actual logical tip.
  const settingsRef = useRef<StoreSettingsResponse | null>(null);
  const sessionGenerationRef = useRef(0);
  const observationEpochRef = useRef(0);
  const nextRefreshRequestRef = useRef(0);
  const latestPublishedRefreshRef = useRef(0);

  const publishSettings = useCallback(
    (next: StoreSettingsResponse, invalidatesPendingRefreshes: boolean): void => {
      const previous = settingsRef.current;
      if (invalidatesPendingRefreshes) observationEpochRef.current += 1;
      settingsRef.current = next;
      setSettings(next);
      // The shell's minimal launch-status projection is a separate read model.
      // Invalidate it for every newly adopted launch observation, including a
      // conflict rebase or GET refresh, but not for unrelated media changes.
      if (previous && previous.publicLaunchMode !== next.publicLaunchMode) {
        setMutationVersion((version) => version + 1);
      }
    },
    []
  );

  useEffect(() => {
    // A session/tenant transition invalidates every completion issued under
    // the preceding token and prevents the previous snapshot from lingering.
    sessionGenerationRef.current += 1;
    observationEpochRef.current += 1;
    latestPublishedRefreshRef.current = nextRefreshRequestRef.current;
    settingsRef.current = null;
    setSettings(null);
    if (!token) return;
    let cancelled = false;
    setStatus('loading');
    adminApi.settings
      .get(token)
      .then((response) => {
        if (cancelled) return;
        if (response.success) {
          settingsRef.current = response.data;
          setSettings(response.data);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const update = useCallback(
    async (
      patch: StoreSettingsPatch,
      expectedOverride?: Partial<StoreSettingsExpectedState>
    ): Promise<ApiResponse<StoreSettingsResponse>> => {
      if (!token) {
        return {
          success: false,
          error: { code: 'UNAUTHORIZED', message: 'Missing admin session' },
        };
      }
      const fields = Object.keys(patch) as StoreSettingsWriteField[];
      // A conflict observation covers only the fields in the rejected patch.
      // Merge it onto the last complete server token so adding another dirty
      // field during recovery can never make this current UI silently fall
      // back to the tokenless compatibility lane.
      const source = expectedOverride
        ? { ...settingsRef.current?.expectedState, ...expectedOverride }
        : settingsRef.current?.expectedState;
      const hasCompleteSource =
        source !== undefined &&
        fields.every(
          (field) => Object.prototype.hasOwnProperty.call(source, field) && source[field] !== undefined
        );
      const expectedState = hasCompleteSource
        ? Object.fromEntries(fields.map((field) => [field, source[field]]))
        : undefined;
      const sessionGeneration = sessionGenerationRef.current;
      // A newly loaded UI against an old Worker may receive no token at runtime;
      // omit it and use the server's documented tokenless-degrade lane.
      const response = await adminApi.settings.update(token, {
        ...patch,
        ...(expectedState ? { expectedState } : {}),
      });
      if (response.success) {
        // A request issued for a previous tenant/session may still resolve
        // after the provider has switched tokens. Return its transport result
        // to that caller, but never publish cross-session state.
        if (sessionGeneration !== sessionGenerationRef.current) return response;
        // Publish only the fields this request committed. Two disjoint PUTs
        // may both succeed and their complete response snapshots may arrive
        // out of order; replacing the whole DTO here would let the older
        // response regress the other card's newer committed state.
        const current = settingsRef.current;
        let next = response.data;
        if (current) {
          const effectivePatch = Object.fromEntries(
            fields.map((field) => [field, response.data[field]])
          ) as Partial<StoreSettingsResponse>;
          const rawPatch = Object.fromEntries(
            fields.map((field) => [field, response.data.expectedState?.[field]])
          ) as Partial<StoreSettingsExpectedState>;
          const expectedState = response.data.expectedState
            ? {
                ...(current.expectedState ?? response.data.expectedState),
                ...rawPatch,
              }
            : undefined;
          next = { ...current, ...effectivePatch, expectedState };
        }
        publishSettings(next, true);
        setStatus('ready');
      }
      return response;
    },
    [publishSettings, token]
  );

  const refresh = useCallback(async (): Promise<ApiResponse<StoreSettingsResponse>> => {
    if (!token) {
      return {
        success: false,
        error: { code: 'UNAUTHORIZED', message: 'Missing admin session' },
      };
    }
    const refreshRequest = nextRefreshRequestRef.current + 1;
    nextRefreshRequestRef.current = refreshRequest;
    const startEpoch = observationEpochRef.current;
    try {
      const response = await adminApi.settings.get(token);
      if (response.success) {
        // A successful PUT/rebase after this GET began is a newer observation;
        // never let the older full snapshot replace it. Among overlapping
        // GETs, the later-started successful request owns the provider even if
        // responses arrive in reverse order. The response is still returned to
        // the recovery caller so its field-scoped CAS can arbitrate safely.
        const publish =
          observationEpochRef.current === startEpoch &&
          refreshRequest > latestPublishedRefreshRef.current;
        if (publish) {
          latestPublishedRefreshRef.current = refreshRequest;
          publishSettings(response.data, false);
          setStatus('ready');
        }
      }
      return response;
    } catch {
      // Keep the last good provider snapshot. The cards own their controlled
      // drafts and render an explicit retry message for this recovery failure.
      return {
        success: false,
        error: { code: 'NETWORK_ERROR', message: 'Could not refresh settings' },
      };
    }
  }, [publishSettings, token]);

  const rebaseExpected = useCallback(
    (observed: Partial<StoreSettingsExpectedState>): void => {
      const current = settingsRef.current;
      if (!current?.expectedState) return;
      const expectedState = { ...current.expectedState, ...observed };
      const media = resolveMediaSettings(expectedState);
      // Keep the provider's effective DTO and its raw token on the same
      // observation. Otherwise a card that becomes clean on convergence can
      // immediately rehydrate from the stale effective DTO and reverse the
      // concurrent winner. Raw null/corrupt values resolve exactly as the
      // Worker GET boundary does.
      publishSettings(
        {
          ...current,
          publicLaunchMode: resolvePublicLaunchMode(expectedState.publicLaunchMode),
          submissionAcknowledgementEnabled:
            expectedState.submissionAcknowledgementEnabled === true,
          imageAttachmentsEnabled: media.imageAttachmentsEnabled,
          maxImagesPerSubmission: media.maxImagesPerQuestion,
          maxImagesPerReply: media.maxImagesPerReply,
          maxImagesPerStaffReply: media.maxImagesPerStaffReply,
          maxImageSizeMb: media.maxImageSizeMb,
          allowedImageFormats: [...media.allowedImageFormats],
          imageModeration: media.imageModeration,
          expectedState,
        },
        true
      );
    },
    [publishSettings]
  );

  const value = useMemo(
    () => ({ status, settings, update, refresh, rebaseExpected }),
    [rebaseExpected, refresh, settings, status, update]
  );

  return (
    <TenantSettingsMutationContext.Provider value={mutationVersion}>
      <TenantSettingsContext.Provider value={value}>{children}</TenantSettingsContext.Provider>
    </TenantSettingsMutationContext.Provider>
  );
}

/** Null only for isolated consumers/tests that are not mounted by AdminApp. */
export function useTenantSettings(): TenantSettingsContextValue | null {
  return useContext(TenantSettingsContext);
}

/** Minimal invalidation seam for consumers that must never receive private settings fields. */
export function useTenantSettingsMutationVersion(): number {
  return useContext(TenantSettingsMutationContext);
}
